import Image from 'next/image';
import Link from 'next/link';
import { awsImage } from '@firsttable/functions';
import type { FilteredRestaurant } from '@discover-data/gql/getters/filteredRestaurantsCarousel';
import { cn } from '@discover-lib/utils';
import Stars from '../rating/stars';

export type FilteredRestaurantCarouselCardProps = {
  restaurant: Pick<
    FilteredRestaurant,
    | 'menuTitle'
    | 'slug'
    | 'gallery'
    | 'rating'
    | 'approvedReviewsCount'
    | 'suburb'
    | 'region'
    | 'cuisines'
  >;
  imagePriority?: boolean;
  className?: string;
};

export default function FilteredRestaurantCarouselCard({
  restaurant,
  imagePriority = false,
  className,
}: FilteredRestaurantCarouselCardProps) {
  const location = restaurant.suburb?.menuTitle || restaurant.region?.menuTitle;
  const cuisine = restaurant.cuisines?.edges?.[0]?.node?.title;
  const metadata = [location, cuisine].filter(Boolean).join(' • ');
  const hasRating = !!restaurant.rating && !!restaurant.approvedReviewsCount;

  return (
    <article
      className={cn(
        'flex w-full flex-col overflow-hidden rounded border border-gray-200 bg-white shadow-sm transition-all active:scale-[0.98] active:opacity-80',
        className
      )}>
      <Link
        href={restaurant.slug || '#'}
        draggable={false}
        className="relative block overflow-hidden no-underline">
        <Image
          src={
            restaurant.gallery
              ? awsImage(restaurant.gallery, 420, 420)
              : '/images/restaurant-placeholder.jpg'
          }
          alt={`Photo of ${restaurant.menuTitle || 'a restaurant'}`}
          width={300}
          height={300}
          priority={imagePriority}
          draggable={false}
          className="h-[140px] w-full object-cover"
        />
      </Link>

      <div className="min-w-0 px-3 py-4">
        <h3 className="m-0 truncate text-base font-semibold leading-tight text-black">
          <Link
            href={restaurant.slug || '#'}
            draggable={false}
            className="text-current no-underline hover:underline">
            {restaurant.menuTitle}
          </Link>
        </h3>

        <p className="mt-3 truncate py-xs text-base font-normal leading-tight text-gray">
          {metadata}
        </p>

        <div className="flex min-h-6 items-center">
          {hasRating && (
            <Link
              href={restaurant.slug ? `${restaurant.slug}#reviews` : '#'}
              draggable={false}
              className="flex items-center gap-2 text-sm font-semibold leading-none text-gray-900 no-underline lg:text-base">
              <Stars count={restaurant.rating} />
              <span className="text-sm">
                {restaurant.rating.toFixed(1)} ({restaurant.approvedReviewsCount})
              </span>
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}
